import { BaseApiService } from './BaseApiService';

export interface DateRange {
  startDate: string;
  endDate: string;
  label: string;
  fileName: string;
}

export interface LocationData {
  location: string;
  group: string;
  weeklySalesToast: number;
  weeklyCatering: number | null;
  weeklyTotal: number;
  weeklyGfs: number | null;
  gfsPercent: number;
  varianceFromAvg: number;
  eightWeekTotalGfs: number | null;
  eightWeekTotalSales: number;
  eightWeekGfsPercentValue?: number | null;
  eightWeekGfsVariance?: number | null;

  // Quarter-over-quarter trend fields (only populated in quarterly view)
  gfsPercentTrend?: number | null; // Change in GFS% from previous quarter (percentage points)
  totalSalesTrend?: number | null; // Change in total sales from previous quarter (percentage)
  totalGfsTrend?: number | null; // Change in total GFS from previous quarter (percentage)
  
  // Quarterly total sales breakdown (only populated in quarterly view)
  quarterlyToastSales?: number | null; // Total toast sales for the quarter
  quarterlyEzCaterSales?: number | null; // Total ezcater sales for the quarter
  
  // First appearance tracking (for filtering new locations from averages)
  firstAppearanceDate?: string | null; // Earliest date this location appears in any data source (YYYY-MM-DD format)
}

export interface SalesTier {
  tierName: string;
  tierRange: string;
  avgGfsPercent: number;
  locationCount: number;
  locations: LocationData[];
}

export interface DashboardData {
  startDate: string;
  endDate: string;
  dateRangeLabel: string;
  salesTiers: SalesTier[];
  availableDateRanges: DateRange[];
}

export interface DataStatus {
  toastDataAvailable: boolean;
  ezcaterDataAvailable: boolean;
  gfsDataAvailable: boolean;
  allDataAvailable: boolean;
  missingDataSources: string[];
  message: string;
  expectedWeeks?: number[];
  availableWeeks?: number[];
  missingWeeks?: number[];
  partialGfsData?: boolean;
}

export class DashboardService extends BaseApiService {
  private static instance: DashboardService;

  private constructor() {
    super();
  }

  public static getInstance(): DashboardService {
    if (!DashboardService.instance) {
      DashboardService.instance = new DashboardService();
    }
    return DashboardService.instance;
  }

  async getDashboardData(startDate: string, endDate: string, sortBy: string = 'sales'): Promise<DashboardData> {
    // Enable caching - smart cache manager will invalidate on file changes
    return this.get<DashboardData>(
      `/api/dashboard/data?startDate=${startDate}&endDate=${endDate}&sortBy=${sortBy}`,
      undefined,
      true
    );
  }

  async getAvailableDateRanges(): Promise<DateRange[]> {
    // Enable caching - smart cache manager will invalidate on weekly file changes
    return this.get<DateRange[]>('/api/dashboard/date-ranges', undefined, true);
  }

  async getDataStatus(startDate: string, endDate: string): Promise<DataStatus> {
    // Enable caching with shorter TTL for status checks
    return this.get<DataStatus>(
      `/api/dashboard/data-status?startDate=${startDate}&endDate=${endDate}`,
      undefined,
      true
    );
  }

  async getAvailableQuarters(): Promise<string[]> {
    // Enable caching - smart cache manager will invalidate on quarterly file changes
    return this.get<string[]>('/api/dashboard/quarters', undefined, true);
  }

  async getQuarterlyDashboardData(quarter: string, year: number, sortBy: string = 'sales'): Promise<DashboardData> {
    // Enable caching - smart cache manager will invalidate on quarterly file changes
    return this.get<DashboardData>(
      `/api/dashboard/quarterly-data?quarter=${quarter}&year=${year}&sortBy=${sortBy}`,
      undefined,
      true
    );
  }

  async getQuarterlyDataStatus(quarter: string, year: number): Promise<DataStatus> {
    // Enable caching with shorter TTL for status checks
    return this.get<DataStatus>(
      `/api/dashboard/quarterly-data-status?quarter=${quarter}&year=${year}`,
      undefined,
      true
    );
  }
}

export default DashboardService.getInstance();

